import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BadgeCheck, Bell, Check, Clock, Eye, EyeOff, Link2, ListChecks, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { kycService } from '../services/kycService';
import socketService from '../services/socketService';
import { adminOrigin, handoffUrl } from '../config/api';
import DepositModal from '../components/DepositModal';
import WithdrawModal from '../components/WithdrawModal';
import TransactionHistoryModal from '../components/TransactionHistoryModal';
import SecurityModal from '../components/SecurityModal';
import KycModal from '../components/KycModal';
import SocialAccountsModal from '../components/SocialAccountsModal';
import NotificationModal from '../components/NotificationModal';
import LanguageSelector from '../components/LanguageSelector';
import Toast from '../components/Toast';
import WalletScreenSkeleton from '../components/WalletScreenSkeleton';
import { useTranslation } from '../i18n/LocaleContext';

// Wallet ("我的") page: flat neutral surfaces, radius follows hierarchy
// (12px cards / 14px rail tiles / full circles for icon buttons), hairline
// borders instead of soft shadows, no decorative shapes.
// Real person portraits (randomuser.me), mixed genders; each user gets a
// stable one derived from their id so the avatar never changes between visits.
const AVATARS = Array.from({ length: 12 }, (_, i) => `/images/avatars/a${i + 1}.jpg`);

const WalletPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    kycService.getStatus()
      .then((data) => { if (alive) setKycStatus(data.kyc ? data.kyc.status : 'none'); })
      .catch(() => { if (alive) setKycStatus('none'); });
    return () => { alive = false; };
  }, []);
  const [toast, setToast] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [unread, setUnread] = useState(0);
  const [modals, setModals] = useState({
    deposit: false,
    withdraw: false,
    history: false,
    security: false,
    kyc: false,
    social: false,
    notifications: false,
  });

  // 领单被 SOCIAL_REQUIRED 拦截后带回 ?social=1，自动打开绑定弹层
  useEffect(() => {
    if (new URLSearchParams(location.search).get('social') === '1') {
      setModals((m) => ({ ...m, social: true }));
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // 后台是独立域名，只有后台角色才在“我的”里露出入口，点击时把会话带过去
  const storedUser = authService.getCurrentUser();
  const storedRole = storedUser?.userType || storedUser?.role;
  const canUsePanel = storedRole === 'admin' || storedRole === 'referral_admin';

  const goAdmin = () => {
    window.location.href = handoffUrl(adminOrigin());
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // 客服未读数：进页面拉一次，在线时实时 +1
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = socketService.connect(token);
    const loadUnread = () => {
      socketService.getMyUnread((res) => {
        if (res?.success) setUnread(Number(res.count || 0));
      });
    };
    const onNew = (message) => {
      if (message.sender_role === 'admin') setUnread((n) => n + 1);
    };

    socket.on('connect', loadUnread);
    socket.on('new-message', onNew);
    if (socket.connected) loadUnread();

    return () => {
      socket.off('connect', loadUnread);
      socket.off('new-message', onNew);
    };
  }, []);

  const fetchProfile = async () => {
    try {
      const data = await userService.getProfile();
      setUserProfile(data.profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };


  const openModal = (modalName) => {
    setModals((m) => ({ ...m, [modalName]: true }));
  };

  const closeModal = (modalName) => {
    setModals((m) => ({ ...m, [modalName]: false }));
  };

  const handleLogout = () => {
    authService.logout();
    setToast({ message: t('logoutSuccess'), type: 'success' });
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    const isWholeNumber = value % 1 === 0;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isWholeNumber ? 0 : 2,
      maximumFractionDigits: isWholeNumber ? 0 : 2
    }).format(value);
    return `$${formatted}`;
  };


  if (loading) {
    return <WalletScreenSkeleton />;
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('goodMorning') : hour < 18 ? t('goodAfternoon') : t('goodEvening');
  const username = userProfile?.username || '';
  const avatar = AVATARS[(Number(userProfile?.id) || 0) % AVATARS.length];

  // 快捷排只留钱相关和客服；身份/社交/订单/流水走下面的设置列表。
  const transferActions = [
    { key: 'add', icon: DepositIcon, label: t('addFunds'), solid: true, action: () => openModal('deposit') },
    { key: 'withdraw', icon: WithdrawIcon, label: t('quickWithdraw'), action: () => openModal('withdraw') },
    { key: 'support', icon: SupportIcon, label: t('support'), action: () => navigate('/cskh') },
  ];

  return (
    <div className="pb-28 bg-canvas min-h-screen font-display">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="max-w-md mx-auto px-5">
        {/* Greeting header */}
        <div className="pt-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-black/[0.06] shrink-0">
              <img src={avatar} alt="" className="w-full h-full object-cover" />
            </span>
            <div>
              <p className="text-[11.5px] text-navy-muted leading-none">{greeting}</p>
              <div className="mt-1 flex items-center gap-1 max-w-[220px]">
                <p className="text-[14px] font-bold text-navy tracking-tight leading-none truncate">{username}</p>
                {kycStatus === 'approved' ? (
                  <span className="shrink-0 w-[15px] h-[15px] rounded-full bg-brand-500 flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                  </span>
                ) : kycStatus === 'pending' ? (
                  <span className="shrink-0 rounded-full bg-amber-50 px-1.5 py-[1px] text-[10px] font-semibold text-amber-600">{t('kycPendingShort')}</span>
                ) : kycStatus ? (
                  <button
                    type="button"
                    onClick={() => openModal('kyc')}
                    className="shrink-0 rounded-full bg-white border border-black/[0.06] px-1.5 py-[1px] text-[10px] font-semibold text-ink-muted"
                  >
                    {t('kycUnverified')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => openModal('notifications')}
              aria-label={t('support')}
              className="relative w-9 h-9 rounded-full bg-white border border-black/[0.05] flex items-center justify-center text-navy"
            >
              <Bell className="w-[17px] h-[17px]" strokeWidth={1.9} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Card carousel — lavender debit + peach credit */}
        <div className="mt-5 flex gap-3 overflow-x-auto scrollbar-hide -mx-5 px-5 pb-1">
          <div className="relative shrink-0 w-[238px] h-[162px] rounded-xl bg-navy overflow-hidden">
            <svg aria-hidden="true" viewBox="0 0 176 176" className="absolute -right-12 -bottom-16 w-44 h-44">
              <circle cx="88" cy="88" r="72" fill="none" stroke="#fff" strokeOpacity="0.07" strokeWidth="30" />
              <circle cx="88" cy="88" r="38" fill="none" stroke="#fff" strokeOpacity="0.05" strokeWidth="10" />
            </svg>
            <div className="relative p-4">
              <div className="flex items-center justify-between">
                <p className="text-[13.5px] font-bold text-white">{t('profitCard')}</p>
                <ContactlessIcon className="w-5 h-5 text-white/60" />
              </div>
              <div className="mt-3.5 flex items-center gap-3">
                <CardChip />
                <div className="min-w-0">
                  <p className="text-[11px] text-white/55">{t('totalEarned')}</p>
                  <div className="mt-1 flex items-center gap-2.5">
                    <span className="tabular text-[23px] leading-none font-extrabold text-white tracking-tight">
                      {hidden ? '••••••' : formatCurrency(userProfile?.total_earnings)}
                    </span>
                    <button type="button" onClick={() => setHidden((v) => !v)} aria-label={t('profitCard')} className="p-0.5 text-white/80">
                      {hidden ? <EyeOff className="w-[17px] h-[17px]" strokeWidth={2.2} /> : <Eye className="w-[17px] h-[17px]" strokeWidth={2.2} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-3.5 left-4 right-4 flex items-center justify-between">
              <span className="text-[13px] font-extrabold italic text-white tracking-tight">VISA</span>
              <span className="tabular text-[11px] font-semibold text-white/55">12/05</span>
            </div>
          </div>

          <div className="relative shrink-0 w-[146px] h-[162px] rounded-xl bg-brand-500 overflow-hidden">
            <svg aria-hidden="true" viewBox="0 0 150 150" className="absolute -right-10 -top-12 w-36 h-36">
              <circle cx="75" cy="75" r="58" fill="none" stroke="#fff" strokeOpacity="0.12" strokeWidth="16" />
            </svg>
            <div className="relative p-4">
              <p className="text-[13.5px] font-bold text-white">{t('rechargeQuota')}</p>
              <p className="mt-6 text-[11px] text-white/55">{t('totalRecharged')}</p>
              <span className="tabular mt-1 block text-[19px] leading-none font-extrabold text-white tracking-tight">
                {hidden ? '••••' : formatCurrency(userProfile?.total_deposits)}
              </span>
            </div>
            <div className="absolute bottom-3.5 left-4 flex items-center">
              <span className="w-6 h-6 rounded-full bg-white/85" />
              <span className="w-6 h-6 -ml-2 rounded-full bg-white/40" />
            </div>
          </div>
        </div>

        {/* Transfer rail — quick money actions */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <p className="text-[14px] font-bold text-navy tracking-tight">{t('transfer')}</p>
            <button type="button" onClick={() => openModal('history')} className="text-[12.5px] font-semibold text-navy-muted">
              {t('seeAll')}
            </button>
          </div>
          <div className="mt-3.5 grid grid-cols-3 gap-3.5 pb-1">
            {transferActions.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.key} type="button" onClick={item.action} className="flex w-full flex-col items-center">
                  <span className={`w-12 h-12 rounded-[14px] flex items-center justify-center ${item.solid ? 'bg-brand-500' : 'bg-white border border-black/[0.05]'}`}>
                    <Icon className={`w-5 h-5 ${item.solid ? 'text-white' : 'text-navy'}`} strokeWidth={2.1} />
                  </span>
                  <span className="mt-1.5 text-[11.5px] font-semibold text-navy max-w-full truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {/* Utilities — full-bleed section, edge-to-edge rows like real app settings lists */}
      <div className="mt-5 max-w-md mx-auto bg-white border-y border-black/[0.05]">
          {[
            { key: 'kyc', icon: BadgeCheck, label: t('kycTitle'), action: () => openModal('kyc') },
            { key: 'social', icon: Link2, label: t('socialTitle'), action: () => openModal('social') },
            { key: 'orders', icon: ListChecks, label: t('ordTitle'), action: () => navigate('/don-hang') },
            { key: 'history', icon: Clock, label: t('txHistory'), action: () => openModal('history') },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={item.action}
                className="w-full flex items-center gap-3.5 px-4 py-3 border-b border-navy/[0.06] active:bg-canvas transition-colors"
              >
                <span className="w-9 h-9 shrink-0 rounded-[10px] bg-canvas flex items-center justify-center">
                  <Icon className="w-[17px] h-[17px] text-navy" strokeWidth={1.9} />
                </span>
                <span className="flex-1 text-left text-[13px] font-semibold text-navy">{item.label}</span>
                <ChevronRight />
              </button>
            );
          })}
          {canUsePanel && (
            <button
              type="button"
              onClick={goAdmin}
              className="w-full flex items-center gap-3.5 px-4 py-3 border-b border-navy/[0.06] active:bg-canvas transition-colors"
            >
              <span className="w-9 h-9 shrink-0 rounded-[10px] bg-canvas flex items-center justify-center">
                <ShieldCheck className="w-[17px] h-[17px] text-navy" strokeWidth={1.9} />
              </span>
              <span className="flex-1 text-left text-[13px] font-semibold text-navy">{t('aAuthEnterAdmin')}</span>
              <ChevronRight />
            </button>
          )}
          <button
            type="button"
            onClick={() => openModal('security')}
            className="w-full flex items-center gap-3.5 px-4 py-3 border-b border-navy/[0.06] active:bg-canvas transition-colors"
          >
            <span className="w-9 h-9 shrink-0 rounded-[10px] bg-canvas flex items-center justify-center">
              <Settings className="w-[17px] h-[17px] text-navy" strokeWidth={1.9} />
            </span>
            <span className="flex-1 text-left text-[13px] font-semibold text-navy">{t('securityCenter')}</span>
            <ChevronRight />
          </button>
          <div className="flex items-center gap-4 px-4 py-3 border-b border-navy/[0.06]">
            <span className="w-9 h-9 shrink-0 rounded-[10px] bg-canvas flex items-center justify-center">
              <GlobeIcon />
            </span>
            <span className="flex-1 text-left text-[13px] font-semibold text-navy">{t('language')}</span>
            <LanguageSelector />
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3.5 px-4 py-3 active:bg-canvas transition-colors"
          >
            <span className="w-9 h-9 shrink-0 rounded-[10px] bg-canvas flex items-center justify-center">
              <LogOut className="w-[17px] h-[17px] text-navy" strokeWidth={1.9} />
            </span>
            <span className="flex-1 text-left text-[13px] font-semibold text-navy">{t('logout')}</span>
            <ChevronRight />
          </button>
      </div>

      <DepositModal isOpen={modals.deposit} onClose={() => closeModal('deposit')} />
      <WithdrawModal isOpen={modals.withdraw} onClose={() => closeModal('withdraw')} balance={userProfile?.balance || 0} />
      <TransactionHistoryModal isOpen={modals.history} onClose={() => closeModal('history')} />
      <SecurityModal isOpen={modals.security} onClose={() => closeModal('security')} />
      <KycModal isOpen={modals.kyc} onClose={() => closeModal('kyc')} />
      <SocialAccountsModal isOpen={modals.social} onClose={() => closeModal('social')} />
      <NotificationModal isOpen={modals.notifications} onClose={() => closeModal('notifications')} unread={unread} />
    </div>
  );
};

// 快捷排三个入口的自定义图标：钱包 / 出金托盘 / 耳麦客服。
function DepositIcon({ className, strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6H18a1 1 0 0 1 1 1v1.5" />
      <path d="M3 8.5V17a2.5 2.5 0 0 0 2.5 2.5H19a2 2 0 0 0 2-2V12a2 2 0 0 0-2-2H5.5A2.5 2.5 0 0 1 3 8.5Z" />
      <circle cx="16.7" cy="14.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function WithdrawIcon({ className, strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 15.5V4.5m0 0-4 4m4-4 4 4" />
      <path d="M4 15.5v2.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.5" />
    </svg>
  );
}

function SupportIcon({ className, strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M4 13.5a8 8 0 0 1 16 0" />
      <rect x="2.8" y="13" width="4.4" height="6.5" rx="2.2" />
      <rect x="16.8" y="13" width="4.4" height="6.5" rx="2.2" />
      <path d="M19 19.5a2.8 2.8 0 0 1-2.8 2.5h-2.7" />
    </svg>
  );
}

function CardChip() {
  return (
    <svg width="36" height="27" viewBox="0 0 36 27" fill="none" aria-hidden="true">
      <rect x="0.75" y="0.75" width="34.5" height="25.5" rx="5" fill="#E9BD54" stroke="#B98F2F" strokeWidth="1.5" />
      <path d="M12 1.5v24M24 1.5v24" stroke="#B98F2F" strokeWidth="1.3" />
      <path d="M1.5 9.5H12M1.5 17.5H12M24 9.5h10.5M24 17.5h10.5" stroke="#B98F2F" strokeWidth="1.3" />
      <rect x="12" y="7.5" width="12" height="12" rx="2.5" fill="#DDB046" stroke="#B98F2F" strokeWidth="1.3" />
    </svg>
  );
}

function ContactlessIcon({ className }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" className={className} aria-hidden="true">
      <path d="M6 7.2a6.5 6.5 0 0 1 0 5.6" />
      <path d="M9.2 5.2a10.5 10.5 0 0 1 0 9.6" />
      <path d="M12.4 3.4a14.5 14.5 0 0 1 0 13.2" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#1F2A44" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#96A0B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export default WalletPage;
